import { devWarn, devError, devInfo } from "@/lib/logger";
import { useEffect, useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import SettingsPage from "@/pages/Settings";
import AdminPage from "@/pages/Admin";
import { PrivacyPage, TermsPage } from "@/pages/Legal";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthEmail";
import { DPAPage, RefundPage, MSAPage, InsurancePage } from "@/pages/Legal2";
import Discover from "@/pages/Discover";
import InstallPrompt from "@/components/InstallPrompt";
import { isNativeShell } from "@/lib/mobileAuth";
import { fetchMe, postSession } from "@/lib/api";

// Stable toast styling (avoids new object on every render → unnecessary Toaster re-renders).
const TOAST_OPTIONS = {
  style: {
    background: "#0a0a0a",
    border: "1px solid #60a5fa",
    color: "#fff",
    fontFamily: "JetBrains Mono",
  },
};

function AuthCallback() {
  const navigate = useNavigate();
  const ranRef = useRef(false);
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/", { replace: true });
      return;
    }

    // Native-shell bridge: if OAuth came from a native app (mh_native=1 in query),
    // this page is currently rendered inside the SYSTEM BROWSER (SFSafariViewController /
    // Chrome Custom Tabs). Redirect back into the native app via the custom URL scheme,
    // where <App/>'s appUrlOpen listener will pick it up and finish the exchange.
    const isNativeReturn = new URLSearchParams(window.location.search).get("mh_native") === "1";
    if (isNativeReturn) {
      // Do NOT exchange session here (system browser cookies wouldn't carry into the app).
      window.location.replace(`moneyhub://auth-callback#session_id=${encodeURIComponent(sid)}`);
      return;
    }

    (async () => {
      try {
        const user = await postSession(sid);
        // Owner login via Google requires the same email 2FA gate as password login.
        // Backend returns { requires_2fa: true, challenge_id } instead of a session cookie.
        if (user?.requires_2fa && user?.challenge_id) {
          navigate(`/auth/email?owner_2fa_challenge=${encodeURIComponent(user.challenge_id)}`, { replace: true });
          return;
        }
        navigate("/dashboard", { replace: true, state: { user } });
      } catch (err) {
        devWarn("Auth callback exchange failed:", err?.message || err);
        navigate("/", { replace: true });
      }
    })();
  }, [navigate]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-[#60a5fa] font-mono">
      <div data-testid="auth-callback-loading" className="animate-pulse-glow text-sm tracking-[0.3em] uppercase">
        ◇ AUTHENTICATING — DO NOT REFRESH ◇
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const [state, setState] = useState(location.state?.user ? "ok" : "checking");
  const [user, setUser] = useState(location.state?.user || null);

  const check = useCallback(async () => {
    try {
      const u = await fetchMe();
      setUser(u);
      setState("ok");
    } catch (err) {
      devWarn("Auth check failed:", err?.message || err);
      setState("no");
    }
  }, []);

  useEffect(() => {
    if (state === "checking") check();
  }, [state, check]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-zinc-200 font-mono text-xs tracking-[0.3em] uppercase">
        Loading
      </div>
    );
  }
  if (state === "no") return <Navigate to="/" replace />;
  return children({ user });
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL race-condition handling: detect session_id during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dpa" element={<DPAPage />} />
      <Route path="/refund" element={<RefundPage />} />
      <Route path="/msa" element={<MSAPage />} />
      <Route path="/insurance" element={<InsurancePage />} />
      <Route path="/discover" element={<Discover />} />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {() => <SettingsPage />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/vault-nc-9k7x2q-panel"
        element={
          <ProtectedRoute>
            {() => <AdminPage />}
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Register native deep-link listener once (Capacitor iOS/Android only).
  // When Emergent OAuth returns to the system browser, the callback page will trigger a
  // moneyhub://auth-callback#session_id=... URL that the OS delivers here via appUrlOpen.
  useEffect(() => {
    if (!isNativeShell()) return;
    let sub;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const { Browser } = await import("@capacitor/browser");
        sub = await CapApp.addListener("appUrlOpen", async (event) => {
          try {
            const u = new URL(event.url);
            const hash = u.hash || "";
            if (hash.includes("session_id=")) {
              // Route into React with the hash so <AuthCallback /> handles the exchange.
              window.location.hash = hash;
              // Close the in-app system browser if still open.
              try {
                await Browser.close();
              } catch (err) {
                // In-app browser may already be closed — non-fatal.
                devWarn("[capacitor] Browser.close skipped:", err?.message || err);
              }
            }
          } catch (err) {
            devWarn("appUrlOpen parse failed:", err?.message || err);
          }
        });
      } catch (err) {
        devWarn("Capacitor app listener not available:", err?.message || err);
      }
    })();
    return () => { sub?.remove?.(); };
  }, []);

  useEffect(() => {
    // Subtle cursor-follow parallax on background watermarks.
    // Respects OS "Reduce Motion" and skips touch devices.
    const wm = document.querySelector(".brand-watermark");
    if (!wm) return;
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isCoarse || reduced) return;
    let raf = 0;
    let px = 0, py = 0, tx = 0, ty = 0;
    const onMove = (e) => {
      // Normalize -1..1 across the viewport, then scale to a max 25px lean
      const nx = (e.clientX / window.innerWidth) * 2 - 1;
      const ny = (e.clientY / window.innerHeight) * 2 - 1;
      tx = nx * 25;
      ty = ny * 25;
      if (!raf) raf = requestAnimationFrame(loop);
    };
    const loop = () => {
      // Ease toward target for silky drift
      px += (tx - px) * 0.08;
      py += (ty - py) * 0.08;
      wm.style.setProperty("--px", `${px.toFixed(2)}px`);
      wm.style.setProperty("--py", `${py.toFixed(2)}px`);
      if (Math.abs(tx - px) > 0.2 || Math.abs(ty - py) > 0.2) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = 0;
      }
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="App">
      <div className="brand-watermark" aria-hidden="true">
        <div className="wm-parallax" style={{ "--depth": 1.2 }}><div className="wm wm-nc wm-1" /></div>
        <div className="wm-parallax" style={{ "--depth": 0.9 }}><div className="wm wm-clark wm-2" /></div>
        <div className="wm-parallax" style={{ "--depth": 1.5 }}><div className="wm wm-vip wm-3" /></div>
        <div className="wm-parallax" style={{ "--depth": 0.7 }}><div className="wm wm-clark wm-4" /></div>
        <div className="wm-parallax" style={{ "--depth": 1.0 }}><div className="wm wm-nc wm-5" /></div>
        <div className="wm-parallax" style={{ "--depth": 1.6 }}><div className="wm wm-vip wm-6" /></div>
        <div className="wm-parallax" style={{ "--depth": 1.3 }}><div className="wm wm-vip wm-7" /></div>
        <div className="wm-parallax" style={{ "--depth": 0.8 }}><div className="wm wm-nc wm-8" /></div>
        <div className="wm-parallax" style={{ "--depth": 1.4 }}><div className="wm wm-clark wm-9" /></div>
      </div>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" toastOptions={TOAST_OPTIONS} />
        <AppRouter />
        <InstallPrompt />
      </BrowserRouter>
    </div>
  );
}

export default App;
